import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import '../styles/PreferentialProcurementDocumentation.scss';
import api from '../services/api';

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const UploadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="17 8 12 3 7 8"/>
    <line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
);

// Per-element display metadata. Adding a new sector's Procurement
// variant means adding one entry here, not a new page file.
const SUBMIT_META = {
  procurement: {
    pageDesc: 'Preferential Procurement — Upload & Calculate Scorecard',
    templateFilename: 'ProcurementDataSheetTemplate.xlsx',
    cardDesc: 'Preferential Procurement Data Sheet (.xlsx)',
    backPath: '/esd/preferential-procurement',
    scorecardPath: '/esd/preferential-procurement/scorecard',
  },
};

const PPSubmitDataSheetPageNew = () => {
  const navigate = useNavigate();
  const { elementKey: rawElementKey } = useParams();
  const elementKey = (rawElementKey || 'procurement').replace(/-/g, '_');
  const meta = SUBMIT_META[elementKey] || SUBMIT_META.procurement;

  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get(`/core/scorecard/download_template/?element=${elementKey}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = meta.templateFilename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Template not found on server.');
    }
  };

  useEffect(() => {
    api.get(`/core/scorecard/get_scorecard/?element=${elementKey}`)
      .then(res => setResult(res.data))
      .catch(() => {});
  }, [elementKey]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('element', elementKey);
      const res = await api.post('/core/scorecard/calculate/', formData);
      setResult(res.data);
    } catch (err) {
      if (err.response?.status === 422) {
        setError('Missing required data. Check Company Information');
      } else {
        setError(err.response?.data?.error || 'Failed to process the data sheet. Check the file format and try again.');
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const maxPoints = (result?.indicators || []).reduce((s, ind) => s + (ind.points || 0), 0);

  return (
    <div className="od-container">
      <div className="od-header">
        <button className="od-back" onClick={() => navigate(meta.backPath)}>← Back</button>
        <div>
          <h1>Submit Data Sheet</h1>
          <p>{meta.pageDesc}</p>
        </div>
      </div>

      <div className="od-section-label">
        Download the template, fill it in, then upload to calculate your scorecard
      </div>

      <div className="od-grid">
        <button className="od-card" onClick={handleDownloadTemplate}>
          <div className="od-card-icon"><DownloadIcon /></div>
          <span className="od-card-label">Download Template</span>
          <span className="od-card-desc">{meta.cardDesc}</span>
        </button>

        <button
          className={`od-card ${result ? 'od-card--uploaded' : ''}`}
          onClick={() => fileInputRef.current.click()}
          disabled={uploading}
        >
          <div className="od-card-icon"><UploadIcon /></div>
          <span className="od-card-label">
            {uploading ? 'Calculating…' : 'Upload Completed Sheet'}
          </span>
          {result && (
            <span className="od-card-status">✓ {result.filename}</span>
          )}
        </button>
      </div>

      <input
        type="file"
        ref={fileInputRef}
        accept=".xlsx"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {error && (
        <div className="od-error">
          {error}
          {error.includes('TMPS') && (
            <button className="od-error-link" onClick={() => navigate('/finance-statutory/manage-tmps')}>
              Update Company Information
            </button>
          )}
        </div>
      )}

      {result && (
        <div className="od-result">
          <div className="od-result-header">
            <strong>Calculation complete</strong>
          </div>
          <div className="od-result-score">
            <span className="od-result-points">{result.total_points}</span>
            {maxPoints > 0 && <span className="od-result-max">/ {maxPoints} points</span>}
          </div>
          <button
            className="od-result-btn"
            onClick={() => navigate(meta.scorecardPath)}
          >
            View Full Scorecard →
          </button>
        </div>
      )}
    </div>
  );
};

export default PPSubmitDataSheetPageNew;
